import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { isOmniCredentials } from '@/react/portainer/settings/sharedCredentials/utils';
import axios, { parseAxiosError } from '@/portainer/services/axios';
import { success as notifySuccess } from '@/portainer/services/notifications';
import { withGlobalError } from '@/react-tools/react-query';

import { GitCredential } from '../../account/git-credentials/types';

import {
  CreateCredentialPayload,
  Credential,
  CredentialType,
  UpdateCredentialPayload,
  GitCredentials,
} from './types';

const queryKeys = {
  allCredentials: () => ['cloud-credentials'] as const,
  credential: (id: number) => [...queryKeys.allCredentials(), id] as const,
};

export async function createCredential(credential: CreateCredentialPayload) {
  try {
    if (isOmniCredentials(credential.credentials)) {
      await axios.get('omni/serviceaccount/validate', {
        params: {
          endpoint: credential.credentials.endpoint,
          serviceAccountKey: credential.credentials.serviceAccountKey,
        },
      });
    }

    if (credential.provider === CredentialType.GIT) {
      const git = credential.credentials as GitCredentials;
      return await axios.post(buildSharedGitCredentialsUrl(), {
        name: credential.name,
        username: git.username,
        password: git.personalAccessToken,
        authorizationType: git.authType,
      });
    }

    return await axios.post(buildUrl(), credential);
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to create credential');
  }
}

export async function getCloudCredentials() {
  try {
    const { data: credentials } = await axios.get<Credential[]>(buildUrl());
    const { data: gitcredentials } = await axios.get<GitCredential[]>(
      buildSharedGitCredentialsUrl()
    );

    const mappedGitCred = gitcredentials.map((cred) => ({
      id: cred.id,
      created: cred.creationDate,
      name: cred.name,
      provider: CredentialType.GIT,
      credentials: {
        authType: cred.authorizationType,
        username: cred.username || '',
        personalAccessToken: '',
      } as GitCredentials,
    }));

    return [...credentials, ...mappedGitCred];
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to get credentials');
  }
}

export async function deleteCredential(credential: Credential) {
  try {
    if (credential.provider === CredentialType.GIT) {
      return await axios.delete<GitCredential[]>(
        buildSharedGitCredentialsUrl(credential.id)
      );
    }
    return await axios.delete<Credential[]>(buildUrl(credential.id));
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to delete credential');
  }
}

export async function getCloudCredential(
  id: number,
  isGitCredential: boolean = false
) {
  try {
    if (isGitCredential) {
      const { data } = await axios.get<GitCredential>(
        buildSharedGitCredentialsUrl(id)
      );
      return {
        id: data.id,
        created: data.creationDate,
        name: data.name,
        provider: CredentialType.GIT,
        credentials: {
          authType: data.authorizationType,
          username: data.username || '',
          personalAccessToken: '',
        },
      };
    }
    const { data } = await axios.get<Credential>(buildUrl(id));

    return data;
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to get credentials');
  }
}

export async function updateCredential(
  credential: Partial<UpdateCredentialPayload>,
  id: number
) {
  try {
    if (credential.provider === CredentialType.GIT) {
      const git = credential.credentials as GitCredentials;
      const { data } = await axios.put(buildSharedGitCredentialsUrl(id), {
        name: credential.name,
        username: git.username,
        password: git.personalAccessToken,
        authorizationType: git.authType,
      });
      return data;
    }
    const { data } = await axios.put(buildUrl(id), credential);
    return data;
  } catch (e) {
    throw parseAxiosError(e as Error, 'Unable to update credential');
  }
}

export function useUpdateCredentialMutation(id: number) {
  const queryClient = useQueryClient();

  return useMutation(
    ({ credential }: { credential: Partial<UpdateCredentialPayload> }) =>
      updateCredential(credential, id),
    {
      onSuccess: (_, data) => {
        notifySuccess(
          'Credentials updated successfully',
          data.credential.name || ''
        );
        return queryClient.invalidateQueries(queryKeys.credential(id));
      },
      meta: {
        error: {
          title: 'Failure',
          message: 'Unable to update credential',
        },
      },
    }
  );
}

export function useDeleteCredentialMutation() {
  const queryClient = useQueryClient();

  return useMutation(deleteCredential, {
    onSuccess: (_, credential) => {
      notifySuccess('Credentials deleted successfully', credential.name);
      return queryClient.invalidateQueries(queryKeys.allCredentials());
    },
    meta: {
      error: {
        title: 'Failure',
        message: 'Unable to delete credential',
      },
    },
  });
}

export function useCloudCredential(
  id: number,
  isGitCredential: boolean = false,
  authorized = true
) {
  return useQuery(
    queryKeys.credential(id),
    () => getCloudCredential(id, isGitCredential),
    {
      cacheTime: 0, // don't cache to make sure the Use SSH key authentication is correctly set
      enabled: id >= 0 && authorized,
      ...withGlobalError('Unable to retrieve cloud credential'),
    }
  );
}

export function useCloudCredentials(queryOptions?: {
  select?: (credentials: Credential[]) => Credential[];
}) {
  return useQuery(
    queryKeys.allCredentials(),
    () => getCloudCredentials(),
    queryOptions
  );
}

export function useCreateCredentialMutation() {
  const queryClient = useQueryClient();

  return useMutation(createCredential, {
    onSuccess: (_, payload) => {
      notifySuccess('Credentials created successfully', payload.name);
      return queryClient.invalidateQueries(queryKeys.allCredentials());
    },
    meta: {
      error: {
        title: 'Failure',
        message: 'Unable to create credential',
      },
    },
  });
}

function buildUrl(credentialId?: number) {
  let url = 'cloud/credentials';
  if (credentialId) {
    url += `/${credentialId}`;
  }
  return url;
}

function buildSharedGitCredentialsUrl(credentialId?: number) {
  let url = 'cloud/gitcredentials';
  if (credentialId) {
    url += `/${credentialId}`;
  }
  return url;
}
